// Illustrations for the home page.
//
// These are NOT the deck marks scaled up. The deck marks are 24×24 line glyphs that say what a deck
// contains; these are small scenes with people in them, and they say what you would be DOING. A
// front door wants warmth more than it wants taxonomy — nobody opens an app to admire a
// classification — so each one shows somebody reading, talking, asking, or being somewhere.
//
// Flat shapes, no outlines, generous curves. Colour comes from three theme tokens and never a
// literal: `currentColor` for the subject (set per area on the wrapper), a wash of it for the
// masses behind, and the page's own ink for the small human details. That is what keeps them
// working across all 44 themes in both light and dark, where a hand-picked palette would clash on
// half of them.
//
// The figures are deliberately faceless — a dot for an eye at most. A drawn face has an age, a
// gender and a mood, and this is a page every learner has to see themselves in.

/** The subject colour at full strength, and the washes behind it. */
const C = 'currentColor';
const WASH = 'color-mix(in srgb, currentColor 26%, transparent)';
const SOFT = 'color-mix(in srgb, currentColor 14%, transparent)';
/** The page's own ink, for details that should read as line work rather than as subject. */
const INK = 'var(--ink)';
const INK_SOFT = 'color-mix(in srgb, var(--ink) 45%, transparent)';

/**
 * A person, drawn as a head and a rounded body.
 *
 * One helper because every scene needs at least one and they must all look like the same species.
 * `lean` tips the body a few degrees, which is most of what makes a flat figure look alive.
 */
function figure(x: number, y: number, scale = 1, fill = C, lean = 0): string {
  return (
    `<g transform="translate(${x} ${y}) scale(${scale}) rotate(${lean})">` +
    // Chubbier body and a bigger round head — cuter proportions, closer to a chibi than a suit.
    `<path d="M-9 8.6c0-6.8 4-10.4 9-10.4s9 3.6 9 10.4z" fill="${fill}"/>` +
    `<circle cx="0" cy="-13.4" r="7.7" fill="${fill}"/>` +
    // A friendly face: two eyes and a little smile, in ink so they read as features on any hue.
    `<circle cx="-2.8" cy="-14.2" r="1.25" fill="${INK}"/>` +
    `<circle cx="2.8" cy="-14.2" r="1.25" fill="${INK}"/>` +
    `<path d="M-2.6 -10.4c1.1 1.5 4.1 1.5 5.2 0" fill="none" stroke="${INK}" stroke-width="1.05" stroke-linecap="round"/>` +
    `</g>`
  );
}

/**
 * The Mynah itself, small, as a recurring character across the scenes.
 *
 * A round body, a bright eye, a stub of a beak and a little wing — the same bird the `ask` scene is
 * built around, shrunk to perch inside the others. It takes `currentColor` like everything else, so
 * it is the tile's own hue and never a literal, and so it survives every theme. `look` flips it to
 * face left; `lean` tips it, which is most of what makes a flat bird look like it is doing something.
 */
function bird(x: number, y: number, scale = 1, look: 1 | -1 = 1, lean = 0): string {
  return (
    `<g transform="translate(${x} ${y}) scale(${scale * look} ${scale}) rotate(${lean})">` +
    // A little crest, so the silhouette reads as a bird at a glance.
    `<path d="M2 -12.5c-0.6-3 0.4-5.2 2.4-6 0.7 2 0.3 4.2-1.4 6z" fill="${C}"/>` +
    // The round body.
    `<path d="M-11 2.5C-11 -5 -5.6 -11 2 -11c7.4 0 12.6 5 12.6 12 0 5.8-4.4 9.5-10.4 9.5H-2.4C-7.4 10.5-11 6.9-11 2.5z" fill="${C}"/>` +
    // A soft belly, lit from the front.
    `<ellipse cx="2.4" cy="4.4" rx="6.6" ry="5.6" fill="var(--bg)" opacity="0.26"/>` +
    // The tucked wing.
    `<path d="M-2 -0.5c-4.4 0.2-7.6 2.8-7.6 6 3.2 0.3 6.4-1.5 8.6-4z" fill="${C}" opacity="0.55"/>` +
    // The bright eye.
    `<circle cx="7.6" cy="-4.4" r="2.5" fill="var(--bg)"/>` +
    `<circle cx="8.3" cy="-4.4" r="1.2" fill="${INK}"/>` +
    // The beak.
    `<path d="M14.4 -2.6l6.4 2-6.4 2.4z" fill="${C}"/>` +
    `</g>`
  );
}

export interface Art {
  /** The whole scene, on a 120×92 stage. */
  svg: string;
}

/**
 * Every scene shares one stage size so they line up down the page without per-item nudging.
 */
export const STAGE = { w: 120, h: 92 };

const SCENES: Record<string, string> = {
  // Someone sitting with a book, characters lifting off the page.
  words:
    `<ellipse cx="60" cy="82" rx="42" ry="6" fill="${SOFT}"/>` +
    `<path d="M26 76V54c0-2 1.6-3.4 3.6-3.4h26c2 0 3.6 1.4 3.6 3.4v22z" fill="${WASH}"/>` +
    `<path d="M94 76V54c0-2-1.6-3.4-3.6-3.4h-26c-2 0-3.6 1.4-3.6 3.4v22z" fill="${WASH}"/>` +
    `<path d="M26 76h68" stroke="${C}" stroke-width="3.4" stroke-linecap="round"/>` +
    figure(60, 46, 1.05) +
    `<circle cx="66" cy="20" r="2.6" fill="${C}" opacity="0.4"/>`,

  // Two people talking, one bubble each.
  length:
    `<ellipse cx="60" cy="82" rx="44" ry="6" fill="${SOFT}"/>` +
    `<path d="M6 34h38c2.2 0 4 1.8 4 4v13c0 2.2-1.8 4-4 4H22l-8 6v-6H6c-2.2 0-4-1.8-4-4V38c0-2.2 1.8-4 4-4z" fill="${WASH}"/>` +
    `<path d="M114 20H80c-2.2 0-4 1.8-4 4v11c0 2.2 1.8 4 4 4h18l7 5v-5h9c2.2 0 4-1.8 4-4V24c0-2.2-1.8-4-4-4z" fill="${SOFT}"/>` +
    `<path d="M10 44h26M10 50h17" stroke="${C}" stroke-width="2.6" stroke-linecap="round" opacity="0.75"/>` +
    `<path d="M84 29h22M84 34h14" stroke="${C}" stroke-width="2.4" stroke-linecap="round" opacity="0.5"/>` +
    figure(36, 74, 1, C, -4) +
    figure(84, 74, 1, WASH, 4),

  // A page of writing with one word lit up in it, which is what the section does: the word you
  // studied, found inside a sentence. The lines are even and quiet so the one that is not reads
  // as the subject rather than as decoration.
  'in-context':
    `<ellipse cx="60" cy="82" rx="40" ry="6" fill="${SOFT}"/>` +
    `<path d="M24 12h72c3 0 5.4 2.4 5.4 5.4v52c0 3-2.4 5.4-5.4 5.4H24c-3 0-5.4-2.4-5.4-5.4v-52C18.6 14.4 21 12 24 12z" fill="${WASH}"/>` +
    `<path d="M29 25h40M29 34h54M29 52h30M29 61h47" stroke="${C}" stroke-width="3.2" stroke-linecap="round" opacity="0.45"/>` +
    `<rect x="62" y="46.5" width="26" height="11" rx="5.5" fill="${C}" opacity="0.22"/>` +
    `<path d="M67 52h16" stroke="${C}" stroke-width="3.4" stroke-linecap="round"/>` +
    `<path d="M75 62.5v5" stroke="${C}" stroke-width="2.6" stroke-linecap="round" opacity="0.6"/>`,

  // One figure with a raised arm, a question hanging over them.
  questions:
    `<ellipse cx="60" cy="82" rx="38" ry="6" fill="${SOFT}"/>` +
    `<path d="M30 20h60c3 0 5.4 2.4 5.4 5.4v22c0 3-2.4 5.4-5.4 5.4H52l-11 8v-8h-11c-3 0-5.4-2.4-5.4-5.4v-22C24.6 22.4 27 20 30 20z" fill="${WASH}"/>` +
    `<path d="M52 33.6a7.6 7.6 0 1 1 8.4 7.6v3.4" fill="none" stroke="${C}" stroke-width="4.4" stroke-linecap="round"/>` +
    `<circle cx="60.6" cy="50" r="2.9" fill="${C}"/>` +
    figure(60, 76, 1.05) +
    `<path d="M70 66c5-2 8-6 8-11" stroke="${C}" stroke-width="4" stroke-linecap="round" fill="none"/>`,

  // A row of shopfronts with somebody standing in one.
  situations:
    `<ellipse cx="60" cy="82" rx="48" ry="6" fill="${SOFT}"/>` +
    `<path d="M8 78V44l14-11 14 11v34z" fill="${WASH}"/>` +
    `<path d="M40 78V38l16-13 16 13v40z" fill="${C}" opacity="0.5"/>` +
    `<path d="M76 78V48l14-11 14 11v30z" fill="${WASH}"/>` +
    `<path d="M4 44l18-14 18 14M36 38l20-16 20 16M72 48l18-14 18 14" fill="none" stroke="${C}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<rect x="50" y="56" width="12" height="22" rx="2" fill="${INK}" opacity="0.18"/>` +
    `<circle cx="18" cy="54" r="3.4" fill="${C}" opacity="0.6"/><circle cx="94" cy="56" r="3.4" fill="${C}" opacity="0.6"/>` +
    figure(88, 76, 0.8, C),

  // A child in bed, a book, a moon.
  stories:
    `<circle cx="96" cy="22" r="11" fill="${WASH}"/>` +
    `<circle cx="91" cy="19" r="9" fill="var(--bg)"/>` +
    `<circle cx="24" cy="16" r="2.2" fill="${C}" opacity="0.5"/><circle cx="40" cy="26" r="1.6" fill="${C}" opacity="0.35"/>` +
    `<path d="M12 78V62c0-3 2.4-5.4 5.4-5.4h85c3 0 5.4 2.4 5.4 5.4v16z" fill="${WASH}"/>` +
    `<path d="M8 78h104" stroke="${C}" stroke-width="3.6" stroke-linecap="round"/>` +
    `<path d="M30 57c0-8 6-13 13-13v13z" fill="${C}" opacity="0.55"/>` +
    figure(46, 54, 0.78, C, -12) +
    `<path d="M58 56l14-5v9l-14 4z" fill="${C}"/><path d="M86 56L72 51v9l14 4z" fill="${C}" opacity="0.6"/>`,

  // A hand of cards being picked from.
  drills:
    `<ellipse cx="60" cy="82" rx="38" ry="6" fill="${SOFT}"/>` +
    `<g transform="rotate(-14 42 56)"><rect x="24" y="34" width="30" height="42" rx="5" fill="${WASH}"/></g>` +
    `<g transform="rotate(-4 60 54)"><rect x="45" y="30" width="30" height="42" rx="5" fill="${SOFT}"/></g>` +
    `<g transform="rotate(9 80 56)"><rect x="66" y="34" width="30" height="42" rx="5" fill="${C}" opacity="0.55"/></g>` +
    `<g transform="rotate(-4 60 54)"><path d="M53 46h14M53 53h9" stroke="${INK}" stroke-width="3" stroke-linecap="round" opacity="0.3"/></g>` +
    `<path d="M92 20l3.4 7 7 3.4-7 3.4-3.4 7-3.4-7-7-3.4 7-3.4z" fill="${C}" opacity="0.75"/>`,

  // Somebody on a summit with a flag.
  test:
    `<ellipse cx="60" cy="82" rx="46" ry="6" fill="${SOFT}"/>` +
    `<path d="M2 78L34 40l16 19 14-17 26 36z" fill="${WASH}"/>` +
    `<path d="M56 78l30-40 32 40z" fill="${C}" opacity="0.45"/>` +
    `<path d="M86 38l-8 9h16z" fill="var(--bg)" opacity="0.5"/>` +
    `<path d="M86 36V16" stroke="${C}" stroke-width="3.4" stroke-linecap="round"/>` +
    `<path d="M86 17l16 5-16 5z" fill="${C}"/>` +
    figure(30, 44, 0.7, C, -6),

  // The bird itself, listening.
  ask:
    `<ellipse cx="60" cy="82" rx="34" ry="6" fill="${SOFT}"/>` +
    `<path d="M42 78c-9 0-16-7-16-16 0-14 11-25 25-25 15 0 27 10 27 24 0 10-7 17-17 17z" fill="${C}" opacity="0.55"/>` +
    `<circle cx="46" cy="30" r="13" fill="${C}"/>` +
    `<circle cx="42" cy="27" r="2.6" fill="var(--bg)"/>` +
    `<path d="M33 32l-11 3 11 4z" fill="${C}" opacity="0.8"/>` +
    `<path d="M60 52c9-2 14-7 14-14" stroke="var(--bg)" stroke-width="3" fill="none" stroke-linecap="round" opacity="0.45"/>` +
    `<path d="M88 26a13 13 0 0 1 0 18M96 20a21 21 0 0 1 0 30" fill="none" stroke="${C}" stroke-width="3.4" stroke-linecap="round" opacity="0.6"/>`,

  // A face in profile, sound leaving it.
  pronounce:
    `<ellipse cx="60" cy="82" rx="34" ry="6" fill="${SOFT}"/>` +
    `<path d="M64 78V56h-9c-3 0-4.6-3-3-5.4l9-14V30c0-9 7-16 16-16h5v64z" fill="${WASH}"/>` +
    `<circle cx="66" cy="32" r="3" fill="${INK}" opacity="0.35"/>` +
    `<path d="M52 58h14" stroke="${C}" stroke-width="3.4" stroke-linecap="round"/>` +
    `<path d="M36 34a17 17 0 0 0 0 24M24 26a29 29 0 0 0 0 40M12 18a41 41 0 0 0 0 56" fill="none" stroke="${C}" stroke-width="3.6" stroke-linecap="round" opacity="0.75"/>`,

  // A list with things ticked off it, and the ticks doing the work.
  known:
    `<ellipse cx="60" cy="82" rx="40" ry="6" fill="${SOFT}"/>` +
    `<path d="M26 76V22c0-2.6 2.2-4.8 4.8-4.8h42c2.6 0 4.8 2.2 4.8 4.8v54z" fill="${WASH}"/>` +
    `<path d="M38 30l4.6 4.6L52 25" fill="none" stroke="${C}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>` +
    `<path d="M38 46l4.6 4.6L52 41" fill="none" stroke="${C}" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>` +
    `<path d="M38 62h5" stroke="${INK_SOFT}" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="M58 30h14M58 46h12M58 62h10" stroke="${INK}" stroke-width="3" stroke-linecap="round" opacity="0.28"/>` +
    `<circle cx="88" cy="24" r="12" fill="${C}"/>` +
    `<path d="M82.5 24l4 4 7.5-7.6" fill="none" stroke="var(--bg)" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round"/>`,

  // A page being written, with the characters coming off the nib.
  made:
    `<ellipse cx="60" cy="82" rx="40" ry="6" fill="${SOFT}"/>` +
    `<path d="M22 78V26c0-2.6 2.2-4.8 4.8-4.8h50c2.6 0 4.8 2.2 4.8 4.8v52z" fill="${WASH}"/>` +
    `<path d="M34 38h30M34 50h34M34 62h20" stroke="${C}" stroke-width="3.4" stroke-linecap="round" opacity="0.75"/>` +
    `<path d="M92 14l12 12-30 30-15 3 3-15z" fill="${C}"/>` +
    `<path d="M62 44l15 15" stroke="var(--bg)" stroke-width="2.6" opacity="0.45"/>`,
};

/**
 * The marks some scenes carry, which belong to the COURSE and not to this file.
 *
 * They were written in as 字, 词 and 写 -- simplified Chinese, in every course, from the years when
 * there was only one. A learner of Japanese got a Chinese 词 on the tile above 単語, and both
 * English courses got characters their reader had not come here for (MYN-162, after MYN-161 moved
 * the wallpaper for the same reason).
 *
 * Held apart from the scenes rather than threaded through them: a scene is a drawing and these are
 * the two or three marks laid over it, so a course that declares none simply gets the drawing --
 * which is what an older corpus, and any course whose writing system has nothing small to show
 * here, should get.
 */
const GARNISH: Record<string, (marks: readonly string[]) => string> = {
  // Characters lifting off the page the reader is holding. Two, at different sizes and opacities,
  // so they read as coming off it rather than as a label.
  words: (m) =>
    (m[0] ? `<text x="34" y="34" font-size="15" fill="${C}" opacity="0.85" font-family="var(--han)">${m[0]}</text>` : '') +
    (m[1] ? `<text x="80" y="26" font-size="12" fill="${C}" opacity="0.55" font-family="var(--han)">${m[1]}</text>` : ''),
  // One mark under the nib, on the page being written.
  made: (m) =>
    m[2] ? `<text x="88" y="76" font-size="14" fill="${C}" opacity="0.6" font-family="var(--han)">${m[2]}</text>` : '',
};

/**
 * Where the Mynah perches in each scene.
 *
 * Kept as a map rather than threaded into the scene strings so the bird is one idea in one place:
 * the drawing is the activity, and the bird is the character watching it happen. `ask` is left out
 * because that whole scene already IS the bird. A scene with no entry simply has no bird.
 */
const BIRD: Record<string, string> = {
  words: bird(93, 69, 0.6, -1),
  length: bird(60, 69, 0.5),
  'in-context': bird(95, 15, 0.56, -1, 6),
  questions: bird(35, 71, 0.52),
  situations: bird(56, 21, 0.48, -1),
  stories: bird(100, 58, 0.48, -1),
  drills: bird(27, 67, 0.5),
  test: bird(104, 41, 0.48, -1),
  pronounce: bird(30, 70, 0.5),
  known: bird(101, 64, 0.48, -1),
  made: bird(33, 70, 0.5),
};

/**
 * A scene, ready to drop into the page. Unknown ids get nothing rather than a wrong picture.
 *
 * `marks` are the course's own, from pair.glyphs. Passing none is a real answer and draws the
 * scene without them.
 */
export function homeArt(id: string, marks: readonly string[] = []): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${STAGE.w} ${STAGE.h}`);
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', 'home-art');
  // The shapes are authored in this file. The marks are not: they come from a corpus, so they are
  // escaped before they reach innerHTML rather than trusted for being ours.
  const garnish = marks.length > 0 ? (GARNISH[id]?.(marks.map(escapeXml)) ?? '') : '';
  svg.innerHTML = (SCENES[id] ?? '') + (BIRD[id] ?? '') + garnish;
  return svg;
}

/** A corpus is data. Three characters from one are still data. */
const escapeXml = (text: string): string =>
  text.replace(/&/gu, '&amp;').replace(/</gu, '&lt;').replace(/>/gu, '&gt;');

/**
 * The theme token that colours each scene.
 *
 * Named tokens rather than fixed colours, and no two NEIGHBOURS share one. That matters more than
 * it sounds: several themes draw primary, accent and info from the same family, so an order that
 * looked varied in the abstract came out as three blues in a row at the top of the page — which is
 * the part everyone sees. Alternating through success, warning and danger guarantees a change of
 * temperature at every step, whatever the theme does with its blues.
 */
const HUES: Record<string, string> = {
  words: 'primary',
  length: 'success',
  // Between success and warning on the page, so neither of its neighbours' colours.
  'in-context': 'info',
  questions: 'warning',
  situations: 'danger',
  stories: 'info',
  drills: 'success',
  ask: 'warning',
  pronounce: 'danger',
  test: 'primary',
  // Ticking things off is the one unambiguously good-news tile on the page.
  known: 'success',
  made: 'info',
};

export function artHue(id: string): string {
  return HUES[id] ?? 'primary';
}
